import React, { useState, useMemo } from "react";
import {
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  useTheme,
} from "@mui/material";
import { useAuth } from "../../../context";
import useFetchObjects from "../../../api/useFetchObjects";
import { useTranslation } from "react-i18next";
import ReportPdfLayout from "../../../Components/ReportPdfLayout";

const RadiologyReport = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const auth = useAuth();
  const token = auth?.user?.token;

  const [reportType, setReportType] = useState("monthly");

  const params = useMemo(() => {
    const p = new URLSearchParams();
    p.append("report_type", reportType);
    return p.toString();
  }, [reportType]);

  const xrayEndpoint = `patient-xray-reports/?${params}`;
  const ultrasoundEndpoint = `ultrasound-reports/?${params}`;
  const echoEndpoint = `echo-reports/?${params}`;

  const { data: xrayData, isLoading: xrayLoading, isError: xrayError, error: xrayErr, refetch: refetchXray } = useFetchObjects(
    ["xray-report", params],
    xrayEndpoint,
    token
  );
  const { data: ultrasoundData, isLoading: ultrasoundLoading, isError: ultrasoundError, refetch: refetchUltrasound } = useFetchObjects(
    ["ultrasound-report", params],
    ultrasoundEndpoint,
    token
  );
  const { data: echoData, isLoading: echoLoading, isError: echoError, refetch: refetchEcho } = useFetchObjects(
    ["echo-report", params],
    echoEndpoint,
    token
  );

  const isLoading = xrayLoading || ultrasoundLoading || echoLoading;
  const hasError = xrayError || ultrasoundError || echoError;
  const errorMsg = xrayErr?.message;

  const handleApply = () => {
    refetchXray();
    refetchUltrasound();
    refetchEcho();
  };

  const blocks = [
    { title: "X-Ray", data: xrayData, totalKey: "total_tests", pendingKey: "pending_tests" },
    { title: "Ultrasound", data: ultrasoundData, totalKey: "total_tests", pendingKey: "pending_tests" },
    { title: "Echo", data: echoData, totalKey: "total_tests", pendingKey: "pending_tests" },
  ];

  return (
    <Grid container maxWidth="xl" sx={{ p: 3, width: "100%" }}>
      <Grid item xs={12}>
        <ReportPdfLayout
          reportTitle={t("reports.radiologyReport")}
          fileName="radiology-report.pdf"
          disabled={isLoading || hasError}
        >
          <Grid container>
            <Grid item xs={12} className="no-print">
              <Typography variant="h5" fontWeight={600} gutterBottom>
                {t("reports.radiologyReport")}
              </Typography>
            </Grid>
            <Grid item xs={12} mb={2} className="no-print">
              <Paper sx={{ p: 2, display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>{t("reports.reportType")}</InputLabel>
                  <Select
                    value={reportType}
                    label={t("reports.reportType")}
                    onChange={(e) => setReportType(e.target.value)}
                  >
                    <MenuItem value="daily">{t("reports.daily")}</MenuItem>
                    <MenuItem value="weekly">{t("reports.weekly")}</MenuItem>
                    <MenuItem value="monthly">{t("reports.monthly")}</MenuItem>
                  </Select>
                </FormControl>
                <Button variant="contained" onClick={handleApply}>
                  {t("reports.apply")}
                </Button>
              </Paper>
            </Grid>
            {hasError && (
              <Grid item xs={12}>
                <Alert severity="error">{errorMsg || t("reports.errorLoading")}</Alert>
              </Grid>
            )}
            {isLoading && (
              <Grid item xs={12} display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Grid>
            )}
            {!isLoading && (
              <Grid item xs={12} container spacing={2}>
                {blocks.map(({ title, data, totalKey, pendingKey }) => (
                  <Grid item xs={12} md={4} key={title}>
                    <Paper sx={{ p: 2, borderLeft: `4px solid ${theme.palette.primary.main}` }}>
                      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        {title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("reports.totalTests")}: {data?.[totalKey] ?? "—"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("reports.pendingTests")}: {data?.[pendingKey] ?? "—"}
                      </Typography>
                      {data?.date_range && (
                        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                          {data.date_range.start} — {data.date_range.end}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </Grid>
        </ReportPdfLayout>
      </Grid>
    </Grid>
  );
};

export default RadiologyReport;
